import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export const useEmailCollection = () => {
  const { user, isAnonymous } = useAuth();
  const [needsEmailCollection, setNeedsEmailCollection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkEmailStatus = async () => {
      if (!user || isAnonymous) {
        setIsLoading(false);
        return;
      }

      // Skip Supabase auth calls in dev mode for mock users
      const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
      const isDevUser = user.id.startsWith('dev-');

      if (isDevMode && isDevUser) {
        // Dev users already have mock email data, no collection needed
        setNeedsEmailCollection(false);
        setIsLoading(false);
        console.log('🚨 DEV MODE: Skipping email collection for dev user');
        return;
      }

      try {
        // Check if user has email in auth.users
        const { data: authData, error: authError } = await supabase.auth.getUser();

        if (authError) {
          console.error('Error fetching user:', authError);
          setIsLoading(false);
          return;
        }

        const hasEmail = authData.user?.email && authData.user.email.trim() !== '';

        // Check if user skipped email collection before
        const hasSkippedBefore = localStorage.getItem(`email-skipped-${user.id}`);

        // Check if this is a recent phone auth user without email
        // Look for users who authenticated via phone OTP (custom auth)
        const provider = authData.user?.app_metadata?.provider;
        const isPhoneUser = provider === 'phone' || !provider; // Custom phone auth might not have provider set
        const createdRecently = authData.user?.created_at
          ? new Date(authData.user.created_at) > new Date(Date.now() - 10 * 60 * 1000) // 10 minutes ago
          : false;

        // Additional check: see if user has phone but no email (phone-first signup)
        const hasPhone = authData.user?.phone && authData.user.phone.trim() !== '';
        const isPhoneOnlyUser = hasPhone && !hasEmail;

        // Show email collection if:
        // 1. No email address AND
        // 2. (Phone auth user created recently OR phone-only user) AND
        // 3. Haven't skipped before
        const shouldShowCollection =
          !hasEmail &&
          (createdRecently || isPhoneOnlyUser) &&
          !hasSkippedBefore;

        console.log('📧 [EmailCollection] Status check:', {
          hasEmail,
          hasPhone,
          isPhoneUser,
          isPhoneOnlyUser,
          createdRecently,
          hasSkippedBefore,
          shouldShowCollection,
          provider
        });

        setNeedsEmailCollection(shouldShowCollection);
      } catch (error) {
        console.error('Error checking email status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkEmailStatus();
  }, [user, isAnonymous]);

  const markEmailCollected = () => {
    setNeedsEmailCollection(false);
  };

  const skipEmailCollection = () => {
    setNeedsEmailCollection(false);
    // Store that user skipped (can show again later in profile)
    if (user?.id) {
      localStorage.setItem(`email-skipped-${user.id}`, 'true');
    }
  };

  const resetEmailCollection = () => {
    // Allow showing the modal again (useful for testing or profile page)
    if (user?.id) {
      localStorage.removeItem(`email-skipped-${user.id}`);
    }
    setNeedsEmailCollection(true);
  };

  return {
    needsEmailCollection,
    isLoading,
    markEmailCollected,
    skipEmailCollection,
    resetEmailCollection,
  };
};